const LOCAL_AUTH_KEY = 'dcs_local_auth_user';

export type LocalAuthUser = {
    email: string;
    firstName: string;
    lastName: string;
    displayName: string;
    role: string;
    gpid: string;
    access_token: string;
    expires_at: number;
};

const isEnabledFlag = (value: unknown) => String(value).toLowerCase() === 'true';

export const isLocalAuthEnabled = () => isEnabledFlag(import.meta.env.VITE_ENABLE_LOCAL_AUTH);

const getConfiguredEmail = () => import.meta.env.VITE_LOCAL_AUTH_EMAIL || 'local@local.test';
const getConfiguredPassword = () => import.meta.env.VITE_LOCAL_AUTH_PASSWORD || 'password';
const getConfiguredRole = () => import.meta.env.VITE_LOCAL_AUTH_ROLE || 'AE&D Admin';
const getConfiguredGpid = () => import.meta.env.VITE_LOCAL_AUTH_GPID || 'local-gpid';
const getConfiguredFirstName = () => import.meta.env.VITE_LOCAL_AUTH_FIRST_NAME || 'Local';
const getConfiguredLastName = () => import.meta.env.VITE_LOCAL_AUTH_LAST_NAME || 'User';

const base64UrlEncode = (value: Record<string, unknown>) => {
    const base64 = btoa(JSON.stringify(value));
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
};

const createLocalJwt = () => {
    const now = Math.floor(Date.now() / 1000);
    const firstName = getConfiguredFirstName();
    const lastName = getConfiguredLastName();
    const displayName = `${firstName} ${lastName}`.trim();
    const role = getConfiguredRole();

    const header = base64UrlEncode({ alg: 'none', typ: 'JWT' });
    const payload = base64UrlEncode({
        ver: 1,
        jti: `local-${now}`,
        iss: 'local-dcs-auth',
        aud: 'rd-dcs-service',
        iat: now,
        exp: now + 8 * 60 * 60,
        cid: 'local-dcs-ui',
        uid: getConfiguredGpid(),
        scp: ['openid', 'profile', 'email', 'pepappdcsroles'],
        auth_time: now,
        sub: getConfiguredEmail(),
        gpid: getConfiguredGpid(),
        GPID: getConfiguredGpid(),
        FirstName: firstName,
        LastName: lastName,
        displayname: displayName,
        locale: 'en_US',
        email: getConfiguredEmail(),
        pepappdcsroles: [role],
        group: ['local-dcs-users'],
    });

    return `${header}.${payload}.local`;
};

export const localLogin = (email: string, password: string): LocalAuthUser | null => {
    if (!isLocalAuthEnabled()) return null;

    const normalizedEmail = email.trim().toLowerCase();
    if (normalizedEmail !== getConfiguredEmail().toLowerCase() || password !== getConfiguredPassword()) {
        return null;
    }

    const firstName = getConfiguredFirstName();
    const lastName = getConfiguredLastName();
    const user: LocalAuthUser = {
        email: getConfiguredEmail(),
        firstName,
        lastName,
        displayName: `${firstName} ${lastName}`.trim(),
        role: getConfiguredRole(),
        gpid: getConfiguredGpid(),
        access_token: createLocalJwt(),
        expires_at: Math.floor(Date.now() / 1000) + 8 * 60 * 60,
    };

    window.localStorage.setItem(LOCAL_AUTH_KEY, JSON.stringify(user));
    return user;
};

export const getLocalAuthUser = (): LocalAuthUser | null => {
    if (!isLocalAuthEnabled()) return null;

    try {
        const rawUser = window.localStorage.getItem(LOCAL_AUTH_KEY);
        if (!rawUser) return null;

        const user = JSON.parse(rawUser) as LocalAuthUser;
        if (!user.access_token || user.expires_at <= Math.floor(Date.now() / 1000)) {
            clearLocalAuth();
            return null;
        }

        return user;
    } catch {
        clearLocalAuth();
        return null;
    }
};

export const getLocalAccessToken = () => getLocalAuthUser()?.access_token || null;

export const isLocalAuthenticated = () => Boolean(getLocalAuthUser());

export const clearLocalAuth = () => {
    window.localStorage.removeItem(LOCAL_AUTH_KEY);
};
